import React from "react";
import {observer} from "mobx-react-lite";
import {Alert, Autocomplete, Checkbox, TextField} from "@mui/material";
import {useSitStores} from "~/stores";
import type {BaselineSource, Category, Company} from "../../../ApiTypes";
import {BaselineSourceValues, category_readable_str} from "../../../ApiTypes";
import {baselineSourceLabel, sitColumnLabel, sitFieldLabel} from "../../../sitLabels";
import {SitField, sitFormTags, SitRead} from "../../../components/design/SitDesignAtoms";
import {roundAmount} from "~/utils/js-utils";

// Money: parse to float and round so stored == shown == sent. Months: integer.
const num = (e: React.ChangeEvent<HTMLInputElement>) => roundAmount(parseFloat(e.target.value));
const int = (e: React.ChangeEvent<HTMLInputElement>) => parseInt(e.target.value) || 0;
const fmt = (v: number | null | undefined) => (Number(v) || 0).toLocaleString();

export const InitiativeSplitForm: React.FC = observer(() => {
    const {initiativeInstanceStore: s, ccStore} = useSitStores();
    const dis = s.disabled;
    const f0 = s.f_c[0];
    const amount = s.splitMode === 'amount';
    // Prefix the unit with "K" when amounts are shown in thousands (see SitConfiguration).
    const ccy = ccStore.getCurrencyCode(f0.instance.currency_id, ccStore.valuesInThousands ? 'K ' : '');
    const kHelp = ccStore.valuesInThousands ? 'Value in thousands (k).' : undefined;
    const children = s.f_c.filter((_f, i) => i > 0);

    // Company / category options come from the (combined) primary's scope.
    const companyChip = (fc: typeof f0) => (
        <label className="sitf">
            <span className="sitf-label">Company</span>
            <Autocomplete
                disabled={dis} multiple selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                size="small" limitTags={1} renderTags={sitFormTags((o: {title: string}) => o.title)}
                options={f0.companyValues ?? ([] as Company[])}
                getOptionLabel={(o) => o.title}
                value={fc.companyValues}
                renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select"/>}
                renderOption={(props, option) =>
                    <li {...props} key={option.id}>
                        <Checkbox checked={(fc.instance.companies?.indexOf(option.id) ?? -1) > -1}/>
                        {option.title}
                    </li>}
                onChange={(_e, value) => fc.companies = value.map((c) => c.id)}/>
        </label>
    );
    const categoryChip = (fc: typeof f0) => (
        <label className="sitf">
            <span className="sitf-label">Category</span>
            <Autocomplete
                disabled={dis} multiple selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                size="small" limitTags={1} renderTags={sitFormTags(category_readable_str)}
                options={f0.categoryValues ?? ([] as Category[])}
                getOptionLabel={(o) => category_readable_str(o)}
                value={fc.categoryValues}
                renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select"/>}
                onChange={(_e, value) => fc.categories = value.map((c) => c.id)}/>
        </label>
    );

    return (
        <>
            {children.length > 0 &&
                <div className="sit-split-toolbar">
                    <div className="sit-type-toggle">
                        <button type="button" className={amount ? '' : 'on'} disabled={dis}
                                onClick={() => s.splitMode = 'percentage'}>
                            {sitFieldLabel('splitPercentage')}
                        </button>
                        <button type="button" className={amount ? 'on' : ''} disabled={dis}
                                onClick={() => s.splitMode = 'amount'}>
                            {sitFieldLabel('splitAmount')}
                        </button>
                    </div>
                </div>}

            {s.w_no_split_companies && <Alert severity="warning" color="error" className="mb-2">
                Saving split is missing for companies:&nbsp;
                {s.w_no_split_companies.map(id => ccStore.getCompanyTitle(id)).join(', ')}.
            </Alert>}
            {s.w_no_split_categories && <Alert severity="warning" color="error" className="mb-2">
                Saving split is missing for categories:&nbsp;
                {s.w_no_split_categories.map(id => ccStore.getCategoryReadableStr(id)).join(', ')}.
            </Alert>}
            {!amount && s.w_saving_split_sum !== undefined && <Alert severity="warning" color="error" className="mb-2">
                Sum of savings percentages is not 100: {s.w_saving_split_sum}
            </Alert>}

            {children.length === 0 &&
                <div className="sit-muted" style={{marginBottom: 8}}>
                    Single company / category — no split needed. Use “+ New saving split” to distribute across
                    participants.
                </div>}

            {/* Percentage mode — share only; amount derived from the primary. */}
            {!amount && children.map((fc) => (
                <div className="sit-split-line" key={fc.index}>
                    <div className="sit-grid">
                        {companyChip(fc)}
                        {categoryChip(fc)}
                        <SitField label="Saving %" units="%">
                            <input type="number" disabled={dis} value={fc.percentage}
                                   onChange={e => fc.percentage = parseFloat(e.target.value) || 0}/>
                        </SitField>
                        <SitRead label={'Saving ' + ccy}
                                 value={fmt(f0.instance.saving_amount * fc.percentage / 100)}/>
                    </div>
                    <button type="button" className="sit-rm-btn" disabled={dis} aria-label="Remove"
                            onClick={() => s.removeFinancial(fc.index)}>✕
                    </button>
                </div>
            ))}

            {/* Amount mode — each participant carries its own full financials. */}
            {amount && children.map((fc) => {
                const recalc = () => {
                    fc.fillSavings(true);
                    fc.fillAvoidances(true);
                    s.refreshPrimaryFromChildren();
                };
                const ofTotal = f0.instance.saving_amount > 0 ? (100 * fc.instance.saving_amount / f0.instance.saving_amount) : 0;
                return (
                    <div className="sit-split-card" key={fc.index}>
                        <div className="sit-split-card-head">
                            {companyChip(fc)}
                            {categoryChip(fc)}
                            <span className="sit-split-card-share">{ofTotal.toFixed(1)}% of total</span>
                            <button type="button" className="sit-rm-btn" disabled={dis} aria-label="Remove"
                                    onClick={() => s.removeFinancial(fc.index)}>✕
                            </button>
                        </div>
                        <div className="sit-grid">
                            <SitField label={'Baseline spend (BP)'} units={ccy} help={kHelp}>
                                <input type="text" inputMode="decimal" disabled={dis}
                                       value={fc.instance.baseline_spend ?? 0}
                                       onChange={e => {
                                           fc.baselineSpend = num(e);
                                           recalc();
                                       }}/>
                            </SitField>
                            <SitField label={sitFieldLabel('baselineSource')}>
                                <select value={fc.instance.baseline_source ?? ''} disabled={dis}
                                        onChange={e => fc.baselineSource = (e.target.value ? Number(e.target.value) : null) as BaselineSource | null}>
                                    <option value="">—</option>
                                    {BaselineSourceValues.map(v => <option key={v}
                                                                           value={v}>{baselineSourceLabel(v)}</option>)}
                                </select>
                            </SitField>
                            <SitField label="Affected period" units="months">
                                <input type="number" disabled={dis} value={fc.instance.affected_months ?? 0}
                                       onChange={e => {
                                           fc.affectedMonths = int(e);
                                           recalc();
                                       }}/>
                            </SitField>
                            <SitField label={'Total ' + sitColumnLabel('costSavings').toLowerCase()} units={ccy}
                                      help={kHelp}>
                                <input type="text" inputMode="decimal" disabled={dis}
                                       value={fc.instance.saving_amount ?? 0}
                                       onChange={e => {
                                           fc.savingAmount = num(e);
                                           recalc();
                                       }}/>
                            </SitField>
                            <SitField label={'Total ' + sitColumnLabel('costAvoidance').toLowerCase()} units={ccy}
                                      help={kHelp}>
                                <input type="text" inputMode="decimal" disabled={dis}
                                       value={fc.instance.avoidance_amount ?? 0}
                                       onChange={e => {
                                           fc.avoidanceAmount = num(e);
                                           recalc();
                                       }}/>
                            </SitField>
                            <SitRead label="% on baseline"
                                     value={(Number(fc.instance.saving_percentage) || 0).toFixed(1) + '%'}/>
                        </div>

                        {/* Negotiation snapshot (per participant) */}
                        <div className="sit-grid" style={{marginTop: 10}}>
                            <SitField label={sitFieldLabel('bpValue')} units={ccy} help={kHelp}>
                                <input type="text" inputMode="decimal" disabled={dis} value={fc.instance.bp_value ?? 0}
                                       onChange={e => {
                                           fc.bpValue = num(e);
                                           s.refreshPrimaryFromChildren();
                                       }}/>
                            </SitField>
                            <SitField label={sitFieldLabel('baselineEvolved')} units={ccy} help={kHelp}>
                                <input type="text" inputMode="decimal" disabled={dis}
                                       value={fc.instance.baseline_evolved ?? 0}
                                       onChange={e => {
                                           fc.baselineEvolved = num(e);
                                           s.refreshPrimaryFromChildren();
                                       }}/>
                            </SitField>
                            <SitField label={sitFieldLabel('firstProposal')} units={ccy} help={kHelp}>
                                <input type="text" inputMode="decimal" disabled={dis}
                                       value={fc.instance.first_proposal ?? 0}
                                       onChange={e => {
                                           fc.firstProposal = num(e);
                                           s.refreshPrimaryFromChildren();
                                       }}/>
                            </SitField>
                            <SitField label={sitFieldLabel('negotiatedValue')} units={ccy} help={kHelp}>
                                <input type="text" inputMode="decimal" disabled={dis}
                                       value={fc.instance.negotiated_value ?? 0}
                                       onChange={e => {
                                           fc.negotiatedValue = num(e);
                                           s.refreshPrimaryFromChildren();
                                       }}/>
                            </SitField>
                            <SitField label={sitFieldLabel('volume')}>
                                <input type="text" inputMode="decimal" disabled={dis} value={fc.instance.volume ?? 0}
                                       onChange={e => {
                                           fc.volume = num(e);
                                           s.refreshPrimaryFromChildren();
                                       }}/>
                            </SitField>
                        </div>

                        {/* Investment / severance (per participant) */}
                        <div className="sit-grid" style={{marginTop: 10}}>
                            <SitField label="Required investment" units={ccy} help={kHelp}>
                                <input type="text" inputMode="decimal" disabled={dis}
                                       value={fc.instance.investment ?? 0}
                                       onChange={e => {
                                           fc.investment = num(e);
                                           s.refreshPrimaryFromChildren();
                                       }}/>
                            </SitField>
                            <SitField label="Required severance" units={ccy} help={kHelp}>
                                <input type="text" inputMode="decimal" disabled={dis} value={fc.instance.severance ?? 0}
                                       onChange={e => {
                                           fc.severance = num(e);
                                           s.refreshPrimaryFromChildren();
                                       }}/>
                            </SitField>
                        </div>
                    </div>
                );
            })}

            <button type="button" className="sit-add-line" disabled={dis} onClick={() => {
                s.addFinancial();
                const added = s.f_c[s.f_c.length - 1];
                // New children inherit the primary currency (splits are single-currency).
                added.currencyId = f0.instance.currency_id;
                if (amount) s.refreshPrimaryFromChildren();
            }}>
                + New saving split
            </button>
        </>
    );
});
